import { ChartFigure } from './ChartFigure';
import { fmtK, horizontalBarWidth } from './geometry';

export interface HBarDatum {
  label: string;
  value: number;
  /** Colour token — a category owns its colour (§7). Defaults to accent. */
  color?: string;
  onSelect?: () => void;
  /**
   * Marks a row as an aggregate of everything outside the chart rather than a
   * peer of the rows beside it — an "Others" total. Excluded from the width
   * reference, because such a row is usually the largest number present and
   * scaling to it flattens every real row into a stub.
   */
  aggregate?: boolean;
}

export interface HorizontalBarProps {
  data: readonly HBarDatum[];
  summary: string;
  /** Value column header for the hidden table. */
  valueHeader?: string;
}

/**
 * §5.3 Document type-mix — DOM horizontal bars, widths normalised to the LARGEST
 * value (not the total), ignoring any row flagged `aggregate`. Each category keeps
 * its own colour token; a row can be a real button when it drills through. Hidden
 * table carries the numbers.
 */
export function HorizontalBar({ data, summary, valueHeader = 'Documents' }: HorizontalBarProps): React.JSX.Element {
  const values = data.map((d) => d.value);
  // The comparison the reader is making is between the real rows, so the scale
  // comes from those alone. An aggregate row still renders — clamped to a full
  // track — it just does not get to define what "full" means.
  const peers = data.filter((d) => d.aggregate !== true).map((d) => d.value);
  const reference = peers.length > 0 ? Math.max(...peers) : undefined;
  return (
    <ChartFigure
      summary={summary}
      columns={['Category', valueHeader]}
      tableCaption={summary}
      rows={data.map((d) => ({ label: d.label, values: { [valueHeader]: fmtK(d.value) } }))}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.map((datum) => {
          const inner = (
            <>
              <span style={{ width: 104, color: 'var(--t2)', flex: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                {datum.label}
              </span>
              <span style={{ flex: 1, height: 14, background: 'var(--hov)', borderRadius: 4, overflow: 'hidden' }}>
                <span
                  className="ds-hbar__fill"
                  style={{
                    display: 'block',
                    width: horizontalBarWidth(datum.value, values, reference),
                    height: '100%',
                    background: datum.color ?? 'var(--accent)',
                    borderRadius: 4,
                    transition: 'width .35s ease',
                  }}
                />
              </span>
              <span style={{ width: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--t1)', fontWeight: 500, fontSize: 12 }}>
                {fmtK(datum.value)}
              </span>
            </>
          );
          const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, borderRadius: 6, padding: '1px 4px', margin: '-1px -4px', width: '100%' } as const;
          return datum.onSelect !== undefined ? (
            <button key={datum.label} type="button" style={{ ...rowStyle, border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left' }} onClick={datum.onSelect}>
              {inner}
            </button>
          ) : (
            <div key={datum.label} style={rowStyle}>
              {inner}
            </div>
          );
        })}
      </div>
    </ChartFigure>
  );
}
